import { createFileRoute, Link } from '@tanstack/react-router';
import { z } from 'zod';
import { useEffect } from 'react';
import AdminLayout from '@/features/admin/layouts/AdminLayout';
import AssistantTodosPage from '@/features/admin/pages/assistant_todos/AssistantTodosPage';
import AdminOrganizationsPage from '@/features/admin/pages/organizations/AdminOrganizationsPage';
import AdminUsersPage from '@/features/admin/pages/users/AdminUsersPage';
import AdminUserEditPage from '@/features/admin/pages/users/AdminUserEditPage';
import AdminUserCreatePage from '@/features/admin/pages/users/AdminUserCreatePage';
import AdminOrganizationDetailPage from '@/features/admin/pages/organizations/AdminOrganizationDetailPage';
import ImportRequestsPage from '@/features/admin/pages/import_requests/ImportRequestsPage';
import DetailPage from '@/features/admin/pages/import_requests/DetailPage';
import ItemCreatePage from '@/features/admin/pages/import_requests/ItemCreatePage';
import ItemEditPage from '@/features/admin/pages/import_requests/ItemEditPage';
import AdminSettingsPage from '@/features/admin/pages/settings/AdminSettingsPage';
import useFetchAdminIam from '@/features/admin/api/useFetchAdminIam';
import Loading from '@/components/Loading';

const adminSearchSchema = z.object({
  tab: z
    .enum(['assistant_todos', 'organizations', 'users', 'import_requests', 'settings'])
    .optional()
    .default('assistant_todos'),
  organizationId: z.coerce.number().optional(),
  userId: z.coerce.number().optional(),
  userAction: z.enum(['new']).optional(),
  importRequestId: z.coerce.number().optional(),
  itemId: z.coerce.number().optional(),
  itemAction: z.enum(['new']).optional(),
});

export const Route = createFileRoute('/admin')({
  component: AdminRoot,
  staticData: { title: '内部管理' },
  validateSearch: adminSearchSchema,
});

function AdminRoot() {
  const { tab, organizationId, userId, userAction, importRequestId, itemId, itemAction } =
    Route.useSearch();
  const navigate = Route.useNavigate();
  const { data: currentUser, isLoading, isError } = useFetchAdminIam();
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const canAccessOrganizations = isSuperAdmin || currentUser?.role === 'admin';
  const restrictedTabs = new Set([
    ...(canAccessOrganizations ? [] : ['organizations']),
    ...(isSuperAdmin ? [] : ['users']),
  ]);

  useEffect(() => {
    if (!isLoading && isSuperAdmin === false && restrictedTabs.has(tab)) {
      navigate({ search: { tab: 'assistant_todos' } });
    }
  }, [isLoading, isSuperAdmin, navigate, tab]);

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex min-h-[360px] items-center justify-center">
          <Loading />
        </div>
      </AdminLayout>
    );
  }

  if (isError || !currentUser) {
    return (
      <AdminLayout>
        <div className="flex min-h-[360px] items-center justify-center">
          <div className="text-center">
            <p className="text-lg font-bold text-main">エラーが発生しました</p>
            <p className="mt-2 text-sm text-sub">管理者情報の読み込みに失敗しました。</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const renderContent = () => {
    switch (tab) {
      case 'assistant_todos':
        return <AssistantTodosPage />;
      case 'users':
        if (!isSuperAdmin) {
          return (
            <div className="flex flex-col gap-2">
              <h1 className="text-2xl font-bold text-main">アクセス権限がありません</h1>
              <p className="text-sm text-sub">管理者ユーザー管理にアクセスする権限がありません。</p>
              <Link
                to="/admin"
                search={{ tab: 'assistant_todos' }}
                className="text-sm text-[#4188ff]"
              >
                トップへ戻る
              </Link>
            </div>
          );
        }
        if (userAction === 'new') {
          return <AdminUserCreatePage />;
        }
        if (userId) {
          return <AdminUserEditPage userId={userId} />;
        }
        return <AdminUsersPage />;
      case 'organizations':
        if (!canAccessOrganizations) {
          return (
            <div className="flex flex-col gap-2">
              <h1 className="text-2xl font-bold text-main">アクセス権限がありません</h1>
              <p className="text-sm text-sub">クライアント管理にアクセスする権限がありません。</p>
              <Link
                to="/admin"
                search={{ tab: 'assistant_todos' }}
                className="text-sm text-[#4188ff]"
              >
                トップへ戻る
              </Link>
            </div>
          );
        }
        if (organizationId) {
          return <AdminOrganizationDetailPage organizationId={organizationId} />;
        }
        return <AdminOrganizationsPage />;
      case 'import_requests':
        if (importRequestId && itemAction === 'new') {
          return <ItemCreatePage importRequestId={importRequestId} />;
        }
        if (importRequestId && itemId) {
          return <ItemEditPage importRequestId={importRequestId} itemId={itemId} />;
        }
        if (importRequestId) {
          return <DetailPage importRequestId={importRequestId} />;
        }
        return <ImportRequestsPage />;
      case 'settings':
        return <AdminSettingsPage />;
      default:
        return <AssistantTodosPage />;
    }
  };

  return <AdminLayout>{renderContent()}</AdminLayout>;
}
